import { IPosition } from '../../domain/dimension';
import { getFileExtension, objectToQueryString, isRelativeUrl } from './url';
import { FitOption } from 'core/creative/nodes';
import { sanitizeUrl } from '../../../lib/sanitizer';

export function getImageOptimizerUrl(url: string, options: IImageOptimizerUrlOptions): string {

    if (!url) {
        throw new Error('No image url provided');
    }

    // Crash if it's not a friendly url
    sanitizeUrl(url);

    // Apply default values
    options = { ...defaults, ...options };

    // No URL to optimizer specified or relative (incl. base64) => Use original image
    if (!sanitizeUrl(options.imageOptimizerOrigin || '') || isRelativeUrl(url)) {
        return url;
    }

    const format = getFormat(url, options.webpSupport);

    // Don't optimize SVG (or when wepb is not supported ignore gifs as well)
    if (format === 'svg' || format === 'gif') {
        return url;
    }

    if (!options.width || !options.height) {
        throw new Error('No size provided');
    }

    const position = options.position
        ? { positionX: options.position.x, positionY: options.position.y }
        : undefined;

    // WARNING: DO NOT CHANGE THE ORDER, DOING SO WILL INVALIDATE CACHING
    const imageOptizerParameters: IImageOptimizerParameters = {
        u: encodeURIComponent(url),
        w: Math.round(options.width),
        h: Math.round(options.height),
        q: options.quality,
        f: format,
        rt: getOptimizerRatioParameter(options.fitOption),
        ...position,
        ...options.cropping
    };

    const query = objectToQueryString(imageOptizerParameters);

    return `${options.imageOptimizerOrigin}/api/image/${options.method}${query}`;
}

export function testWebP(): Promise<boolean> {
    return new Promise(res => {
        const webP = new Image();
        webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
        webP.onload = webP.onerror = () => res(webP.height === 2);
    });
}

export function getFormat(url: string, webPSupport: boolean = false): ImageFormats {
    const originalFormat = getImageExtension(url);

    // SVG can't be converted to webp
    if (originalFormat === 'svg') {
        return originalFormat;
    }
    // WebP not supported
    else if (!webPSupport) {
        return !originalFormat || originalFormat === 'webp' ? 'png' : originalFormat;
    }

    return 'webp';
}

function getOptimizerRatioParameter(fitOption?: FitOption): undefined | 'cover' | 'contain' {
    // Distort requires the image optimizer to contain the image
    if (fitOption === 'distort') {
        return 'contain';
    }
    return fitOption;
}

export function getImageExtension(url: string): ImageFormats | undefined {
    const format = getFileExtension(url);

    switch (format) {
        case 'jpg':
            return 'jpeg';
        case 'webp':
        case 'jpeg':
        case 'gif':
        case 'png':
        case 'svg':
            return format;
    }

}

type ImageFormats = 'webp' | 'jpeg' | 'gif' | 'png' | 'svg';

/**
 * Parameters supported by the optimizer service
 */
interface IImageOptimizerParameters {

    // Url to original image
    u: string;

    // Formats supported to optimize
    f: 'webp' | 'jpeg' | 'png';

    // Quality
    q?: number;

    // Size
    w: number;
    h: number;

    // Image Position parameters
    positionX?: number;
    positionY?: number;

    // Cropping parameters
    x1?: number;
    x2?: number;
    y1?: number;
    y2?: number;

    /**
     * How siez should be calculated (like css background-size).
     * Undefined meens true distort image returned to the size given by w & h
     */
    rt?: 'contain' | 'cover';
}

// Note, this is used in widget-editor.component as well but raw text. It should match this interface
export interface IImageOptimizerUrlOptions {
    webpSupport?: boolean;
    imageOptimizerOrigin?: string;
    width: number;
    height: number;
    fitOption?: FitOption;
    cropping?: { x1: number, x2: number, y1: number, y2: number };
    position?: IPosition;
    quality?: number;
    format?: 'webp' | 'jpeg' | 'gif' | 'png' | 'svg';
    method?: 'optimize' | 'optimizepositionedimage';
}

export const defaults: Partial<IImageOptimizerUrlOptions> = {
    webpSupport: false,
    imageOptimizerOrigin: (self.process && process.env?.IMAGE_OPTIMIZER_ORIGIN) as unknown as string,
    method: 'optimize',
    quality: 90,
};
